import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { trackCTAClick, trackSignUpStart, trackPreviewStart, trackEvent } from '../utils/analytics';

const SECTION_NAMES = ['hero', 'action', 'image', 'feedback', 'social', 'bottom'];

export default function WelcomePage() {
  const navigate = useNavigate();
  const sectionRefs = useRef([]);
  const viewedSections = useRef(new Set());
  const [copied, setCopied] = useState(false);

  // Track section visibility — measures how far visitors scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target);
            const name = SECTION_NAMES[idx];
            if (name && !viewedSections.current.has(name)) {
              viewedSections.current.add(name);
              trackEvent('welcome_section_view', { section: name, page_path: '/welcome' });
            }
          }
        });
      },
      { threshold: 0.35 },
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const setSectionRef = useCallback(
    (idx) => (el) => {
      sectionRefs.current[idx] = el;
    },
    [],
  );

  const handleShare = async () => {
    trackEvent('welcome_share_click', { page_path: '/welcome' });
    const shareData = {
      title: 'Andlegur',
      text: 'A private space to figure things out.',
      url: 'https://andlegur.com/welcome',
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* user cancelled */
      }
    } else {
      await navigator.clipboard.writeText(shareData.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <Helmet>
        <title>Welcome to Andlegur | A Private Space to Figure Things Out</title>
        <meta
          name="description"
          content="Trying to figure something out? Andlegur is a private space to reflect, get clarity, and make sense of decisions, relationships, career, and feelings — free and pressure-free."
        />
        <meta
          name="keywords"
          content="andlegur, private reflection, personal clarity, figure things out, journaling, self-reflection, decision making, emotional clarity"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://andlegur.com/welcome" />
        <meta property="og:title" content="Welcome to Andlegur" />
        <meta
          property="og:description"
          content="A private space to figure things out. Try it free — no pressure."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://andlegur.com/welcome" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Welcome to Andlegur" />
        <meta
          name="twitter:description"
          content="Trying to figure something out? Start here."
        />
      </Helmet>

      {/* Keyframes for left/right pointing arrows */}
      <style>{`
        @keyframes point-right {
          0%, 100% { transform: translateX(0); opacity: 0.7; }
          50% { transform: translateX(10px); opacity: 1; }
        }
        @keyframes point-left {
          0%, 100% { transform: translateX(0); opacity: 0.7; }
          50% { transform: translateX(-10px); opacity: 1; }
        }
        @keyframes alt-point-right {
          0%, 35%, 65%, 100% { transform: translateX(0); opacity: 0.35; }
          50% { transform: translateX(10px); opacity: 1; }
        }
        @keyframes alt-point-left {
          0%, 35%, 65%, 100% { transform: translateX(0); opacity: 0.35; }
          50% { transform: translateX(-10px); opacity: 1; }
        }
      `}</style>

      <div className="bg-[#05091B] font-serif text-indigo-100">
        {/* ─── SECTION 1 · Hero ─── */}
        <section
          ref={setSectionRef(0)}
          className="relative min-h-screen flex flex-col overflow-hidden"
        >
          {/* Top bar */}
          <nav className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-5">
            <Link
              to="/"
              className="text-indigo-200 font-serif text-xl tracking-wider hover:text-white transition-colors"
            >
              Andlegur
            </Link>
            <Link
              to="/login"
              className="text-indigo-400 hover:text-indigo-200 text-sm transition-colors"
            >
              Sign in
            </Link>
          </nav>

          {/* Background image + overlays */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%2033.webp"
              alt=""
              className="w-full h-full object-cover opacity-14 mix-blend-screen"
              loading="eager"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-linear-to-br from-[#05091B]/30 via-transparent to-[#05091B]" />
            <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]/50" />
          </div>

          {/* Hero content */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-5 sm:px-10 pb-20">
            <p className="text-indigo-300/80 text-xs sm:text-sm uppercase tracking-[0.3em] mb-6 sm:mb-8">
              Welcome
            </p>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl xl:text-7xl font-bold text-white leading-[1.08] mb-6 sm:mb-8 max-w-3xl">
              Trying to figure
              <br />
              something out?
            </h1>

            <p className="text-2xl sm:text-3xl lg:text-4xl text-indigo-100 leading-relaxed max-w-2xl mb-7 sm:mb-9">
              Andlegur — A private space to figure things out.
            </p>

            <p className="text-lg sm:text-xl text-indigo-50 leading-relaxed max-w-2xl">
              Whether it's a decision, a relationship, your career, or just a feeling you
              can't name — this is where you start making sense of it.
            </p>

            {/* Scroll hint */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-50">
              <svg
                className="w-6 h-6 text-indigo-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </div>
          </div>
        </section>

        {/* ─── SECTION 2 · Action Trigger ─── */}
        <section
          ref={setSectionRef(1)}
          className="min-h-[70vh] flex items-center justify-center px-5 sm:px-10 py-16 sm:py-24"
        >
          <div className="max-w-2xl text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-7">
              Try it. Then be real with us.
            </h2>

            <span className="inline-block bg-amber-500/12 border border-amber-400/25 text-amber-200 text-sm sm:text-base px-5 py-2 rounded-full mb-10 sm:mb-12">
              ✨ Free to try — no pressure
            </span>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  trackCTAClick('welcome_start_using', '/welcome');
                  trackSignUpStart();
                  navigate('/register');
                }}
                className="bg-indigo-600 hover:bg-indigo-500 text-white text-lg font-semibold py-4 px-9 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-indigo-700/40"
                aria-label="Start trying Andlegur — create your free account"
              >
                Start Trying Andlegur →
              </button>

              <button
                onClick={() => {
                  trackCTAClick('welcome_try_preview', '/welcome');
                  trackPreviewStart();
                  navigate('/try');
                }}
                className="border border-indigo-300/60 text-indigo-100 hover:bg-indigo-900/30 text-base font-semibold py-3.5 px-8 rounded-2xl transition-all duration-300"
                aria-label="Try preview mode without creating an account"
              >
                Try Preview First →
              </button>
            </div>
          </div>
        </section>

        {/* ─── SECTION 3 · Image Break ─── */}
        <section
          ref={setSectionRef(2)}
          className="relative h-[60vh] sm:h-[70vh] flex items-center justify-center overflow-hidden"
        >
          <img
            src="/web_images/Andlegur%20web%2020.webp"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-[#05091B]/80" />

          <div className="relative z-10 text-center px-6 max-w-2xl">
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-snug">
              Stop for 5 seconds.
              <br />
              Try it.
              <br />
              Then tell us the truth.
            </p>

            <button
              onClick={() => {
                trackCTAClick('welcome_image_register', '/welcome');
                trackSignUpStart();
                navigate('/register');
              }}
              className="mt-10 bg-indigo-600 hover:bg-indigo-500 text-white text-base sm:text-lg font-semibold py-3.5 px-8 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-indigo-700/40"
              aria-label="Register to try Andlegur"
            >
              Register to Try →
            </button>
          </div>
        </section>

        {/* ─── SECTION 4 · Feedback ─── */}
        <section
          ref={setSectionRef(3)}
          className="min-h-[60vh] flex items-center justify-center px-5 sm:px-10 py-16 sm:py-24"
        >
          <div className="max-w-2xl text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-7">
              Your honest feedback matters more than anything.
            </h2>

            <p className="text-base sm:text-lg text-indigo-100 leading-relaxed max-w-xl mx-auto">
              We didn't build Andlegur to impress you. We built it to help you. And the
              only way we know if it's working — is if you tell us the truth.
            </p>

            {/* Animated scroll-down arrow */}
            <div className="mt-14 flex flex-col items-center gap-1">
              <svg
                className="w-10 h-10 sm:w-14 sm:h-14 text-indigo-400 animate-bounce"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7-7-7" />
              </svg>
              <svg
                className="w-10 h-10 sm:w-14 sm:h-14 text-indigo-400/60 animate-bounce [animation-delay:150ms]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7-7-7" />
              </svg>
              <svg
                className="w-10 h-10 sm:w-14 sm:h-14 text-indigo-400/30 animate-bounce [animation-delay:300ms]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </section>

        {/* ─── SECTION 5 · Social / Share ─── */}
        <section
          ref={setSectionRef(4)}
          className="min-h-[45vh] flex items-center justify-center px-5 sm:px-10 py-16 sm:py-20"
        >
          <div className="max-w-xl text-center">
            <p className="text-purple-300 text-sm uppercase tracking-[0.25em] mb-4">
              Share
            </p>

            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-snug mb-8">
              Know someone who might need a space like this?
            </h2>

            <div className="flex items-center justify-center gap-3 sm:gap-5">
              {/* Left arrow — fires first */}
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-purple-300 shrink-0"
                style={{ animation: 'alt-point-right 1.6s ease-in-out infinite' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5-5 5M6 12h12" />
              </svg>

              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2.5 border border-purple-300 text-indigo-100 hover:bg-indigo-900/25 text-base font-semibold py-3 px-7 rounded-2xl transition-all duration-300"
                aria-label="Share Andlegur with someone"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
                {copied ? 'Link Copied!' : 'Share Andlegur'}
              </button>

              {/* Right arrow — fires second (0.8s delay = half of 1.6s cycle) */}
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-purple-300 shrink-0"
                style={{ animation: 'alt-point-left 1.6s ease-in-out 0.8s infinite' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 17l-5-5 5-5M18 12H6" />
              </svg>
            </div>
          </div>
        </section>

        {/* ─── SECTION 6 · Bottom Image + Final CTA ─── */}
        <section
          ref={setSectionRef(5)}
          className="relative h-[65vh] sm:h-[75vh] flex items-center justify-center overflow-hidden"
        >
          <img
            src="/web_images/Andlegur%20web%2025.webp"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-[#05091B]/65" />

          <div className="relative z-10 text-center px-6 max-w-2xl">
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-snug mb-10">
              Be honest. That's all we ask.
            </p>

            <div className="flex items-center justify-center gap-3 sm:gap-5">
              {/* Left arrow pointing right → toward the button */}
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-300 animate-[point-right_1s_ease-in-out_infinite] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5-5 5M6 12h12" />
              </svg>

              <button
                onClick={() => {
                  trackCTAClick('welcome_bottom_start', '/welcome');
                  trackSignUpStart();
                  navigate('/register');
                }}
                className="bg-indigo-600 hover:bg-indigo-500 text-white text-lg font-semibold py-4 px-9 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-indigo-700/40"
                aria-label="Start using Andlegur"
              >
                Start Using Andlegur →
              </button>

              {/* Right arrow pointing left ← toward the button */}
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-300 animate-[point-left_1s_ease-in-out_infinite] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 17l-5-5 5-5M18 12H6" />
              </svg>
            </div>

            <p className="mt-5 text-green-300 text-sm">
              Private · Free · Your space, your pace
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
